import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Paper, Alert } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Kontext
import { AuthContext } from '../context/AuthContext';

const validationSchema = Yup.object({
  email: Yup.string().email('Ungültige E-Mail-Adresse').required('E-Mail ist erforderlich'),
  password: Yup.string().required('Passwort ist erforderlich')
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error, loading } = useContext(AuthContext);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema,
    onSubmit: async values => {
      const erfolgreich = await login(values.email, values.password);

      // Nur bei erfolgreicher Anmeldung weiterleiten. Vorher wurde immer
      // navigiert – bei falschem Passwort sah man deshalb kurz das Dashboard
      // aufblitzen, bevor es zurück zum Login ging.
      if (erfolgreich) {
        const ziel = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
        navigate(ziel && ziel !== '/login' ? ziel : '/', { replace: true });
      }
    }
  });

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              height: 56,
              width: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              mb: 1
            }}
          >
            <LockOutlinedIcon />
          </Box>
          <Typography component="h1" variant="h5">
            Anmelden
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-Mail-Adresse"
              name="email"
              autoComplete="email"
              autoFocus
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Passwort"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 1 }}
              disabled={loading}
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </Button>

            {/*
              Es gibt keine Selbstregistrierung mehr: Konten werden von einem
              Administrator in der Benutzerverwaltung angelegt.
            */}
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              Sie haben noch keinen Zugang? Bitte wenden Sie sich an Ihren Administrator.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
